import { useState } from "react";
import useSWR from "swr";
import Modal from "react-modal";
import { MdClose } from "react-icons/md";
import { FiChevronLeft, FiChevronRight, FiMapPin, FiUser, FiMaximize, FiSquare } from "react-icons/fi";
import fetcher from "../../utils/fetcher";
import ProductImage from "../ProductImage";
import HomeCarousel from "./HomeCarousel";
import styles from "../../styles/home.module.css";

// Shown only until the admin adds real stories from the dashboard
const PLACEHOLDER_STORIES = [
  { title: "هدية زواج مميزة", sample: "مَوَدَّة وَرَحْمَة" },
  { title: "أبيات للوالدين", sample: "وَبِالْوَالِدَيْنِ إِحْسَانًا" },
  { title: "اسم العائلة بحب", sample: "آل فَذَكِّرْ" },
  { title: "ذكرى رحلة عمرة", sample: "لَبَّيْكَ اللَّهُمَّ" },
];

const StoryModal = ({ story, onClose, onStart }) => {
  const [index, setIndex] = useState(0);
  const images = story?.images ?? [];
  const current = images[index];

  const go = (dir) => setIndex((i) => (i + dir + images.length) % images.length);

  const details = story
    ? [
        { icon: FiMaximize, label: "مقاس الحائط",  value: story.wallSize },
        { icon: FiSquare,   label: "مقاس اللوحة", value: story.paintingSize },
        { icon: FiUser,     label: "العميل",      value: story.customerName },
        { icon: FiMapPin,   label: "البلد",       value: story.country },
      ].filter((d) => d.value)
    : [];

  return (
    <Modal
      isOpen={!!story}
      onRequestClose={onClose}
      className={styles.storyModal}
      overlayClassName={styles.storyOverlay}
    >
      {story && (
        <>
          <button type="button" className={styles.storyClose} onClick={onClose} aria-label="إغلاق">
            <MdClose />
          </button>

          {images.length > 0 && (
            <div className={styles.storyGallery}>
              <ProductImage url={current?.imageUrl} alt={story.title} className={styles.storyMainImg} />

              {images.length > 1 && (
                <>
                  <button type="button" className={`${styles.storyArrow} ${styles.storyArrowNext}`} onClick={() => go(1)} aria-label="التالي">
                    <FiChevronLeft />
                  </button>
                  <button type="button" className={`${styles.storyArrow} ${styles.storyArrowPrev}`} onClick={() => go(-1)} aria-label="السابق">
                    <FiChevronRight />
                  </button>

                  <div className={styles.storyThumbs}>
                    {images.map((img, i) => (
                      <button
                        type="button"
                        key={img.id}
                        onClick={() => setIndex(i)}
                        className={`${styles.storyThumb} ${i === index ? styles.storyThumbActive : ""}`}
                      >
                        <ProductImage url={img.imageUrl} alt="" />
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          <div className={styles.storyContent}>
            <h3 className={styles.storyTitle}>{story.title}</h3>

            {details.length > 0 && (
              <div className={styles.storyDetails}>
                {details.map(({ icon: Icon, label, value }) => (
                  <div key={label} className={styles.storyDetail}>
                    <Icon className={styles.storyDetailIcon} />
                    <span className={styles.storyDetailLabel}>{label}</span>
                    <span className={styles.storyDetailValue}>{value}</span>
                  </div>
                ))}
              </div>
            )}

            {story.description && <p className={styles.storyDesc}>{story.description}</p>}

            <button type="button" className={styles.storyCta} onClick={() => { onClose(); onStart?.(); }}>
              صمم لوحتك الخاصة
            </button>
          </div>
        </>
      )}
    </Modal>
  );
};

const StoriesSection = ({ onStart }) => {
  const [openStory, setOpenStory] = useState(null);

  const { data } = useSWR(`${process.env.REACT_APP_BACKEND_URL}/api/stories`, fetcher, {
    revalidateOnFocus: false,
    errorRetryCount: 2,
    dedupingInterval: 10000,
  });

  const stories = Array.isArray(data) ? data : [];

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>قصص تصاميم خاصة</h2>

      <HomeCarousel>
        {stories.length > 0
          ? stories.map((story) => {
              const meta = [story.customerName, story.country].filter(Boolean).join(" · ");
              return (
                <div key={story.id} className={`${styles.card} ${styles.storyCard}`} onClick={() => setOpenStory(story)}>
                  <div className={styles.cardImg}>
                    {story.images?.[0] ? (
                      <ProductImage url={story.images[0].imageUrl} alt={story.title} loading="lazy" />
                    ) : (
                      <div className={styles.cardFrame}>
                        <span className={styles.calligraphy}>{story.title}</span>
                      </div>
                    )}
                  </div>

                  <div className={styles.cardBody}>
                    <p className={styles.cardTitle}>{story.title}</p>
                    <div className={styles.cardBadge}>{meta || "تصميم خاص"}</div>
                  </div>
                </div>
              );
            })
          : PLACEHOLDER_STORIES.map((story, i) => (
              <div key={i} className={`${styles.card} ${styles.storyCard}`} onClick={onStart}>
                <div className={styles.cardImg}>
                  <div className={styles.cardFrame}>
                    <span className={styles.calligraphy}>{story.sample}</span>
                  </div>
                </div>

                <div className={styles.cardBody}>
                  <p className={styles.cardTitle}>{story.title}</p>
                  <div className={styles.cardBadge}>تصميم خاص</div>
                </div>
              </div>
            ))}
      </HomeCarousel>

      {/* key resets the gallery to the first image for each story */}
      <StoryModal key={openStory?.id ?? "none"} story={openStory} onClose={() => setOpenStory(null)} onStart={onStart} />
    </section>
  );
};

export default StoriesSection;
